import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { fetchMe } from '../api/auth'
import { fetchDepartments, type Department, type Product } from '../api/catalog'
import { ApiError } from '../api/client'
import { customersApi, type Customer } from '../api/customers'
import { dealStatusTone, dealsApi, type Deal, type DealBody } from '../api/deals'
import { userDirectoryApi, type DirectoryUser } from '../api/userDirectory'
import { fetchSelectableProducts } from '../lib/productSelection'
import { useFeatureFlag } from '../lib/featureFlags'
import {
  Button,
  Callout,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  DateField,
  FormGrid,
  LoadingState,
  Page,
  PageHeader,
  Select,
  StatusPill,
  TextField,
  useConfirm,
  usePrompt,
} from '../components/ui'

/**
 * R5-01 (D-01) — scheda Trattativa: cliente o lead, commerciale, prodotti e offerta, importo e data del preventivo,
 * scadenza, probabilità e note. Esiti: Vinta (crea la pratica in bozza precompilata), Persa con motivo, Riapri.
 * Attiva solo con il flag «trattative.scheda»; i permessi veri sono sul server.
 */
interface FormState {
  title: string
  customerId: number | null
  leadId: number | null
  salesUserId: string
  departmentId: string
  productIds: number[]
  offerDescription: string
  quoteAmount: string
  quoteDate: string
  expiresOn: string
  probability: string
  notes: string
}

function customerLabel(c: Customer): string {
  if (c.customerType === 'company') return c.companyName ?? c.customerCode
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || c.customerCode
}

const today = () => new Date().toISOString().slice(0, 10)

function toForm(d: Deal): FormState {
  return {
    title: d.title,
    customerId: d.customerId,
    leadId: d.leadId,
    salesUserId: String(d.salesUserId),
    departmentId: d.departmentId === null ? '' : String(d.departmentId),
    productIds: d.productIds,
    offerDescription: d.offerDescription ?? '',
    quoteAmount: d.quoteAmount === null ? '' : String(d.quoteAmount),
    quoteDate: d.quoteDate ?? '',
    expiresOn: d.expiresOn ?? '',
    probability: d.probability === null ? '' : String(d.probability),
    notes: d.notes ?? '',
  }
}

export function DealDetailPage() {
  const flagOn = useFeatureFlag('trattative.scheda')
  const { id: idParam } = useParams()
  const isNew = idParam === undefined || idParam === 'new'
  const id = isNew ? null : Number(idParam)
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const confirm = useConfirm()
  const prompt = usePrompt()

  const [deal, setDeal] = useState<Deal | null>(null)
  const [form, setForm] = useState<FormState | null>(null)
  const [loading, setLoading] = useState(!isNew)
  const [off, setOff] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [busy, setBusy] = useState(false)
  const [isAdmin, setIsAdmin] = useState(false)
  const [meId, setMeId] = useState<number | null>(null)

  const [users, setUsers] = useState<DirectoryUser[] | null>(null)
  const [departments, setDepartments] = useState<Department[]>([])
  const [products, setProducts] = useState<Product[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerHits, setCustomerHits] = useState<Customer[]>([])

  useEffect(() => {
    fetchMe().then(me => {
      setIsAdmin(me !== null && ['Administrator', 'EqualAdmin'].includes(me.role))
      setMeId(me?.id ?? null)
    }).catch(() => setIsAdmin(false))
    // Agent e Operator non leggono la rubrica (403): il commerciale resta chi crea la trattativa.
    userDirectoryApi.list({ activeOnly: true, limit: 500 }).then(setUsers).catch(() => setUsers(null))
    fetchDepartments().then(setDepartments).catch(() => setDepartments([]))
  }, [])

  // Caricamento della trattativa o modulo nuovo precompilato da ?leadId e ?customerId (dal lead).
  useEffect(() => {
    if (isNew) {
      const leadId = Number(params.get('leadId')) || null
      const customerId = Number(params.get('customerId')) || null
      setForm({
        title: '', customerId, leadId, salesUserId: '', departmentId: '', productIds: [], offerDescription: '',
        quoteAmount: '', quoteDate: today(), expiresOn: '', probability: '', notes: '',
      })
      return
    }
    let cancelled = false
    setLoading(true)
    dealsApi.get(id as number)
      .then(d => { if (!cancelled) { setDeal(d); setForm(toForm(d)) } })
      .catch(e => {
        if (cancelled) return
        if (e instanceof ApiError && e.status === 404) setOff(true)
        else setError(e instanceof Error ? e.message : 'Errore nel caricamento.')
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id, isNew, params])

  const departmentId = form?.departmentId ? Number(form.departmentId) : undefined
  useEffect(() => {
    fetchSelectableProducts(departmentId).then(setProducts).catch(() => setProducts([]))
  }, [departmentId])

  const customerId = form?.customerId ?? null
  useEffect(() => {
    if (customerId === null) { setCustomer(null); return }
    customersApi.get(customerId).then(setCustomer).catch(() => setCustomer(null))
  }, [customerId])

  useEffect(() => {
    const q = customerSearch.trim()
    if (q.length < 2) { setCustomerHits([]); return }
    const t = setTimeout(() => {
      customersApi.list({ search: q, page: 1, size: 10 }).then(r => setCustomerHits(r.items)).catch(() => setCustomerHits([]))
    }, 300)
    return () => clearTimeout(t)
  }, [customerSearch])

  const editable = isNew || deal?.status === 'Aperta'
  const productNames = useMemo(() => new Map(products.map(p => [p.id, p.name])), [products])

  if (!flagOn || off) {
    return (
      <Page>
        <PageHeader title="Trattativa" icon="handshake" back={{ to: '/deals', label: 'Trattative' }} />
        <Callout tone="info" role="note">
          {flagOn ? 'Trattativa non trovata o non visibile.' : 'La scheda Trattativa è spenta: si accende dal flag «trattative.scheda».'}
        </Callout>
      </Page>
    )
  }
  if (loading || !form) {
    return <Page><PageHeader title="Trattativa" icon="handshake" back={{ to: '/deals', label: 'Trattative' }} /><LoadingState /></Page>
  }

  function toggleProduct(pid: number, on: boolean) {
    if (!form) return
    setForm({ ...form, productIds: on ? [...form.productIds, pid] : form.productIds.filter(x => x !== pid) })
  }

  async function save() {
    if (!form) return
    const amount = form.quoteAmount.trim() === '' ? null : Number(form.quoteAmount.replace(',', '.'))
    const probability = form.probability.trim() === '' ? null : Number(form.probability)
    if (!form.title.trim()) { setError('Il titolo è obbligatorio.'); return }
    if (form.customerId === null && form.leadId === null) { setError('Indica il cliente o il lead.'); return }
    if (amount !== null && (Number.isNaN(amount) || amount < 0)) { setError('Importo non valido.'); return }
    if (probability !== null && (Number.isNaN(probability) || probability < 0 || probability > 100)) {
      setError('La probabilità va da 0 a 100.'); return
    }
    const body: DealBody = {
      title: form.title.trim(),
      customerId: form.customerId,
      leadId: form.leadId,
      salesUserId: form.salesUserId ? Number(form.salesUserId) : null,
      departmentId: form.departmentId ? Number(form.departmentId) : null,
      productIds: form.productIds,
      offerDescription: form.offerDescription.trim() || null,
      quoteAmount: amount,
      quoteDate: form.quoteDate || null,
      expiresOn: form.expiresOn || null,
      probability,
      notes: form.notes.trim() || null,
    }
    setBusy(true)
    setError('')
    try {
      if (isNew) {
        const r = await dealsApi.create(body)
        navigate(`/deals/${r.id}`, { replace: true })
      } else {
        await dealsApi.update(id as number, body)
        const d = await dealsApi.get(id as number)
        setDeal(d); setForm(toForm(d)); setNotice('Trattativa salvata.')
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Salvataggio non riuscito.')
    } finally {
      setBusy(false)
    }
  }

  async function win() {
    if (!deal) return
    const ok = await confirm({
      title: 'Trattativa vinta',
      message: 'Si crea la pratica in bozza con cliente, commerciale e prodotto della trattativa. Continuare?',
      confirmLabel: 'Vinta, crea la pratica',
      destructive: false,
    })
    if (!ok) return
    setBusy(true); setError('')
    try {
      const r = await dealsApi.win(deal.id)
      if (r.productWarning) {
        await confirm({
          title: 'Pratica creata senza prodotto',
          message: r.productWarning,
          confirmLabel: 'Apri la pratica',
          destructive: false,
        })
      }
      navigate(`/contracts/${r.contractId}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operazione non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  async function lose() {
    if (!deal) return
    const reason = await prompt({
      title: 'Trattativa persa',
      message: 'Indica il motivo: resta nella scheda e nei report.',
      label: 'Motivo',
      required: true,
      multiline: true,
      confirmLabel: 'Segna come persa',
    })
    if (reason === null) return
    await act(() => dealsApi.lose(deal.id, reason), 'Trattativa segnata come persa.')
  }

  async function reopen() {
    if (!deal) return
    await act(() => dealsApi.reopen(deal.id), 'Trattativa riaperta.')
  }

  async function remove() {
    if (!deal) return
    const ok = await confirm({
      title: 'Eliminare la trattativa?',
      message: 'La trattativa viene eliminata. La pratica eventualmente creata resta.',
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    setBusy(true)
    try {
      await dealsApi.remove(deal.id)
      navigate('/deals', { replace: true })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Eliminazione non riuscita.')
      setBusy(false)
    }
  }

  async function act(run: () => Promise<unknown>, done: string) {
    if (!deal) return
    setBusy(true); setError(''); setNotice('')
    try {
      await run()
      const d = await dealsApi.get(deal.id)
      setDeal(d); setForm(toForm(d)); setNotice(done)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Operazione non riuscita.')
    } finally {
      setBusy(false)
    }
  }

  const salesOptions = users ?? []
  const salesKnown = form.salesUserId === '' || salesOptions.some(u => String(u.id) === form.salesUserId)

  return (
    <Page>
      <PageHeader
        title={isNew ? 'Nuova trattativa' : deal?.title ?? 'Trattativa'}
        icon="handshake"
        back={{ to: '/deals', label: 'Trattative' }}
        actions={deal ? (
          <StatusPill tone={dealStatusTone(deal)}>{deal.status === 'Aperta' && deal.isExpired ? 'Scaduta' : deal.status}</StatusPill>
        ) : undefined}
      />
      <div className="d-flex flex-column gap-3">
        {error && <Callout tone="error">{error}</Callout>}
        {notice && <Callout tone="success">{notice}</Callout>}
        {deal?.status === 'Persa' && deal.lostReason && (
          <Callout tone="warning">Persa: {deal.lostReason}</Callout>
        )}
        {deal?.contractId && (
          <Callout tone="success">
            Vinta: <Link to={`/contracts/${deal.contractId}`}>apri la pratica in bozza</Link>.
          </Callout>
        )}
        {deal && (
          <div className="d-flex gap-2 flex-wrap">
            {deal.status === 'Aperta' && (
              <>
                <Button variant="filled" icon="check" disabled={busy} onClick={() => void win()}>Vinta, crea la pratica</Button>
                <Button variant="outlined" icon="close" disabled={busy} onClick={() => void lose()}>Persa</Button>
              </>
            )}
            {deal.status === 'Persa' && (
              <Button variant="tonal" icon="undo" disabled={busy} onClick={() => void reopen()}>Riapri</Button>
            )}
            {isAdmin && (
              <Button variant="text" icon="delete" disabled={busy} onClick={() => void remove()}>Elimina</Button>
            )}
          </div>
        )}

        <Card>
          <CardHeader title="Cliente e commerciale" />
          <CardContent>
            <FormGrid columns={2}>
              <div>
                <TextField label="Titolo" maxLength={200} required disabled={!editable} value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                {form.customerId !== null ? (
                  <div className="d-flex align-items-center gap-2">
                    <span>Cliente: <Link to={`/customers/${form.customerId}`}>{customer ? customerLabel(customer) : `n. ${form.customerId}`}</Link></span>
                    {editable && (
                      <Button variant="text" onClick={() => setForm({ ...form, customerId: null })}>Cambia</Button>
                    )}
                  </div>
                ) : editable ? (
                  <>
                    <TextField label="Cerca cliente" placeholder="Nome, ragione sociale o codice" value={customerSearch}
                      onChange={e => setCustomerSearch(e.target.value)} />
                    {customerHits.length > 0 && (
                      <div className="d-flex flex-column mt-1">
                        {customerHits.map(c => (
                          <Button key={c.id} variant="text" onClick={() => {
                            setForm({ ...form, customerId: c.id }); setCustomerSearch(''); setCustomerHits([])
                          }}>
                            {customerLabel(c)} ({c.customerCode})
                          </Button>
                        ))}
                      </div>
                    )}
                  </>
                ) : <span>Nessun cliente</span>}
                {form.leadId !== null && (
                  <div className="md-typescale-body-small mt-1">
                    Dal <Link to={`/leads/${form.leadId}`}>lead n. {form.leadId}</Link>
                    {form.customerId === null && ': senza cliente la pratica creata dalla vittoria non avrà il cliente.'}
                  </div>
                )}
              </div>
              <div>
                {users ? (
                  <Select label="Commerciale" disabled={!editable} value={form.salesUserId}
                    onChange={e => setForm({ ...form, salesUserId: e.target.value })}>
                    <option value="">{isNew ? 'Io' : '—'}</option>
                    {!salesKnown && <option value={form.salesUserId}>Utente n. {form.salesUserId}</option>}
                    {salesOptions.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                  </Select>
                ) : (
                  <TextField label="Commerciale" disabled
                    value={form.salesUserId === '' || Number(form.salesUserId) === meId ? 'Io' : `Utente n. ${form.salesUserId}`} />
                )}
              </div>
              <div>
                <Select label="Reparto" disabled={!editable} value={form.departmentId}
                  onChange={e => setForm({ ...form, departmentId: e.target.value, productIds: [] })}>
                  <option value="">Tutti</option>
                  {departments.filter(d => d.isActive).map(d => <option key={d.id} value={String(d.id)}>{d.name}</option>)}
                </Select>
              </div>
            </FormGrid>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Preventivo" />
          <CardContent>
            <FormGrid columns={4}>
              <div>
                <TextField label="Importo (€)" inputMode="decimal" disabled={!editable} value={form.quoteAmount}
                  onChange={e => setForm({ ...form, quoteAmount: e.target.value })} />
              </div>
              <div>
                <DateField kind="date" label="Data del preventivo" disabled={!editable} value={form.quoteDate}
                  onChange={e => setForm({ ...form, quoteDate: e.target.value })} />
              </div>
              <div>
                <DateField kind="date" label="Scadenza" disabled={!editable} value={form.expiresOn}
                  onChange={e => setForm({ ...form, expiresOn: e.target.value })} />
              </div>
              <div>
                <TextField label="Probabilità (%)" inputMode="numeric" disabled={!editable} value={form.probability}
                  onChange={e => setForm({ ...form, probability: e.target.value.replace(/\D/g, '') })} />
              </div>
            </FormGrid>
            <div className="mt-3">
              <TextField label="Offerta" multiline rows={3} maxLength={4000} disabled={!editable} value={form.offerDescription}
                onChange={e => setForm({ ...form, offerDescription: e.target.value })} />
            </div>
            <div className="mt-3">
              <div className="md-typescale-title-small mb-2">Prodotti</div>
              {editable ? (
                products.length === 0 ? <span className="md-typescale-body-small">Nessun prodotto disponibile.</span> : (
                  <FormGrid columns={3}>
                    {products.map(p => (
                      <div key={p.id}>
                        <Checkbox id={`deal-product-${p.id}`} label={p.name} checked={form.productIds.includes(p.id)}
                          onChange={e => toggleProduct(p.id, e.target.checked)} />
                      </div>
                    ))}
                  </FormGrid>
                )
              ) : (
                <span>{form.productIds.length === 0 ? '—' : form.productIds.map(pid => productNames.get(pid) ?? `n. ${pid}`).join(', ')}</span>
              )}
            </div>
            <div className="mt-3">
              <TextField label="Note" multiline rows={3} maxLength={4000} disabled={!editable} value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
            {editable && (
              <div className="mt-3 d-flex gap-2">
                <Button variant="text" disabled={busy} onClick={() => (isNew || !deal ? navigate('/deals') : setForm(toForm(deal)))}>Annulla</Button>
                <Button variant="filled" disabled={busy} onClick={() => void save()}>{isNew ? 'Crea trattativa' : 'Salva'}</Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}
